import { Icon } from "@templates/design-system";
import { cn } from "@newxsystems/shared";
import type { BlockLink } from "@newxsystems/types";
import type { AnchorHTMLAttributes, ComponentType, ReactNode } from "react";
import { useId } from "react";
import { getComponent } from "../../../../client";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockLogo } from "../../../shared/components/BlockLogo/BlockLogo";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IFooterStandardProps } from "./FooterStandard.props";

type RegisteredLink = ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;

type FooterLinkProps = {
  Link?: RegisteredLink;
  children?: ReactNode;
  className?: string;
  link: BlockLink;
};

const FooterLink = ({ Link, children, className, link }: FooterLinkProps) => {
  if (!Link) {
    return null;
  }

  const opensNewTab = link.openInNewTab === true;

  return (
    <Link
      aria-label={link.accessibilityLabel ?? link.label}
      className={cn(
        "inline-flex min-h-11 min-w-0 items-center [overflow-wrap:anywhere] text-[17px]/[1.55] text-foreground underline decoration-1 underline-offset-4 transition-colors duration-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
        className,
      )}
      href={link.href}
      rel={opensNewTab ? "noopener noreferrer" : undefined}
      target={opensNewTab ? "_blank" : undefined}
    >
      {children ?? link.label}
    </Link>
  );
};

export const FooterStandard = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  secondaryLinks,
  socialLinks,
}: IFooterStandardProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const identity = useId();
  const hasContact = Boolean(contact?.email || contact?.phone || contact?.address);
  const hasLogo = Boolean(logo && Link);
  const hasDescription = Boolean(description);
  const hasBrand = Boolean(hasLogo || hasDescription || hasContact);
  const hasNavigation = Boolean(navigationGroups?.length);
  const hasSocialLinks = Boolean(socialLinks?.length);
  const hasMainContent = Boolean(hasBrand || hasNavigation || hasSocialLinks);
  const hasBottomContent = Boolean(secondaryLinks?.length || copyright);

  return (
    <BlockContainer
      as="footer"
      disableSpacing
      fullWidth
      innerClassName="py-12 md:py-16"
      parentClass="border-t border-foreground/20 bg-background"
    >
      <div className="flex flex-col gap-12 md:flex-row md:gap-8 lg:gap-12">
        {hasBrand ? (
          <div className="min-w-0 flex-1 md:flex-[1.15]">
            {logo && Link ? (
              <Link
                aria-label="Home"
                className="inline-flex h-12 w-56 max-w-full rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
                href="/"
              >
                <BlockLogo
                  className="h-full w-full overflow-visible"
                  imageClassName="!object-contain object-left"
                  loading="eager"
                  media={logo}
                  priority
                />
              </Link>
            ) : null}
            {description ? (
              <BlockRichText
                className={cn(
                  "max-w-[38ch] text-[17px]/[1.55] text-onSurface-500 [overflow-wrap:anywhere]",
                  hasLogo && "mt-7",
                  "[&_a]:text-electric-blue [&_a]:underline [&_a]:underline-offset-4 [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-offset-3 [&_a]:focus-visible:outline-ring",
                  "[&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc",
                )}
                content={description}
              />
            ) : null}
            {hasContact ? (
              <address
                className={cn(
                  "flex flex-col items-start gap-2 not-italic",
                  (hasLogo || hasDescription) && "mt-8",
                )}
              >
                {contact?.email && Link ? (
                  <Link
                    className="inline-flex min-h-11 min-w-0 max-w-full items-center [overflow-wrap:anywhere] text-[17px]/[1.55] text-foreground underline decoration-1 underline-offset-4 transition-colors duration-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
                    href={`mailto:${contact.email}`}
                  >
                    {contact.email}
                  </Link>
                ) : null}
                {contact?.phone && Link ? (
                  <Link
                    className="inline-flex min-h-11 min-w-0 max-w-full items-center [overflow-wrap:anywhere] text-[17px]/[1.55] text-foreground underline decoration-1 underline-offset-4 transition-colors duration-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
                    href={`tel:${contact.phone}`}
                  >
                    {contact.phone}
                  </Link>
                ) : null}
                {contact?.address ? (
                  <p className="max-w-[32ch] text-[17px]/[1.55] text-onSurface-500 [overflow-wrap:anywhere]">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
        ) : null}

        {hasNavigation ? (
          <nav aria-label="Footer navigation" className="min-w-0 flex-[2]">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(8rem,1fr))]">
              {navigationGroups?.map((group, groupIndex) => {
                const headingId = `${identity}-navigation-${groupIndex}`;

                return (
                  <div className="min-w-0" key={`${group.header}-${groupIndex}`}>
                    <h2
                      className="max-w-[20ch] font-mono text-[12px]/[1.45] font-semibold [overflow-wrap:anywhere]"
                      id={headingId}
                    >
                      {group.header}
                    </h2>
                    <ul
                      aria-labelledby={headingId}
                      className="mt-4 flex flex-col items-start gap-2"
                    >
                      {group.links.map((link, linkIndex) => (
                        <li key={`${link.href}-${linkIndex}`} className="min-w-0 max-w-full">
                          <FooterLink Link={Link} link={link} />
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </nav>
        ) : null}

        {hasSocialLinks ? (
          <nav aria-label="Social links" className="min-w-0 flex-1 md:flex-[1]">
            <h2 className="font-mono text-[12px]/[1.45] font-semibold">Social links</h2>
            <ul className="mt-4 flex flex-col items-start gap-3">
              {socialLinks?.map((socialLink, index) => (
                <li className="min-w-0 max-w-full" key={`${socialLink.link.href}-${index}`}>
                  <FooterLink
                    Link={Link}
                    className="min-h-11 max-w-full items-center gap-3 rounded-full border border-foreground px-4 py-2 font-mono text-[12px]/[1.45] no-underline hover:bg-foreground hover:text-background"
                    link={socialLink.link}
                  >
                    {socialLink.icon ? <Icon className="size-4" name={socialLink.icon} /> : null}
                    <span className="[overflow-wrap:anywhere]">{socialLink.link.label}</span>
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>

      {hasBottomContent ? (
        <div
          className={cn(
            "flex flex-col gap-4 border-t border-foreground/20 pt-6 md:flex-row md:items-baseline md:justify-between md:gap-8",
            hasMainContent && "mt-12 md:mt-16",
          )}
        >
          {secondaryLinks?.length ? (
            <nav aria-label="Secondary navigation" className="min-w-0">
              <ul className="flex flex-wrap gap-x-6 gap-y-2">
                {secondaryLinks.map((link, index) => (
                  <li key={`${link.href}-${index}`}>
                    <FooterLink Link={Link} link={link} />
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
          {copyright ? (
            <p className="font-mono text-[12px]/[1.45] text-onSurface-500 [overflow-wrap:anywhere] md:text-end">
              {copyright}
            </p>
          ) : null}
        </div>
      ) : null}
    </BlockContainer>
  );
};

FooterStandard.displayName = "Footer::FooterStandard";
